import type { FloorplannerStore } from "../../../store/floorplannerStore";
import type { SymbolType, WallType } from "../../../types/floorplannerTypes";
import {
  isUserModelProductListId,
  userModelProductListId,
} from "../userModels/userModel3DCommerce";
import { resolveUserModel3DUrl } from "../userModels/userModel3DUrls";
import type { CatalogDictionaryEntry } from "./product3DTypes";
import {
  resolveWallFaceSurfaceRef,
  wallHasPerSideFaceMaterials,
} from "../../../utils/wallFaceSides";

/**
 * A product-like row in the floorplan Product List — catalog Products or
 * placed user 3D models. Surfaces / Ready materials do not qualify.
 */
export type PlacedCatalogProduct = {
  productId: string;
  name: string;
  brand?: string;
  supplierName?: string;
  imageUrl?: string;
  /** Unit retail price from catalog dictionary or stamped user-model commerce. */
  price?: number;
  /**
   * Currency of `price` for user models. Catalog prices are always SEK.
   * Used when converting to the project display currency.
   */
  currency?: string;
  /** Product / store URL for user models (and future catalog links). */
  storeUrl?: string;
  /** Distinguishes catalog Mongo ids from `user-model:{id}` list rows. */
  source?: "catalog" | "userModel";
  quantity: number;
  /** Symbol ids that reference this product (for selection from the Product List). */
  symbolIds: string[];
  /** Wall ids that use this product as wallpaper (also selectable from the list). */
  wallIds: string[];
};

type CatalogProductSource = {
  symbolsMap: { values: () => IterableIterator<SymbolType> };
  wallsMap?: { values: () => IterableIterator<WallType> };
  catalogDictionary?: {
    productsById?: Record<string, CatalogDictionaryEntry>;
  };
};

function upsertPlacedProduct(
  byId: Map<string, PlacedCatalogProduct>,
  params: {
    productId: string;
    name?: string;
    brand?: string;
    supplierName?: string;
    imageUrl?: string;
    price?: number;
    currency?: string;
    storeUrl?: string;
    source?: "catalog" | "userModel";
    dictEntry?: CatalogDictionaryEntry;
    symbolId?: string;
    wallId?: string;
  },
): void {
  const {
    productId,
    name,
    brand,
    supplierName,
    imageUrl,
    price,
    currency,
    storeUrl,
    source = "catalog",
    dictEntry,
    symbolId,
    wallId,
  } = params;
  const existing = byId.get(productId);
  if (existing) {
    existing.quantity += 1;
    if (symbolId) existing.symbolIds.push(symbolId);
    if (wallId) existing.wallIds.push(wallId);
    return;
  }

  const unitPrice =
    typeof price === "number" && Number.isFinite(price)
      ? price
      : typeof dictEntry?.price === "number" && Number.isFinite(dictEntry.price)
        ? dictEntry.price
        : undefined;

  byId.set(productId, {
    productId,
    name: name?.trim() || dictEntry?.name?.trim() || "Untitled product",
    brand: brand ?? dictEntry?.brand,
    supplierName: supplierName ?? dictEntry?.supplierName,
    imageUrl: imageUrl ?? dictEntry?.imageUrl,
    price: unitPrice,
    currency:
      currency?.trim() ||
      (source === "catalog" && unitPrice != null ? "SEK" : undefined),
    storeUrl,
    source,
    quantity: 1,
    symbolIds: symbolId ? [symbolId] : [],
    wallIds: wallId ? [wallId] : [],
  });
}

/**
 * Collect unique catalog Products and placed user 3D models for the Product List.
 * Quantity is for UI only; callers that attach lineage to a UserItem should use
 * {@link getPlacedCatalogProductIds} (catalog ids only).
 */
export function collectPlacedCatalogProducts(
  store: CatalogProductSource | FloorplannerStore,
): PlacedCatalogProduct[] {
  const byId = new Map<string, PlacedCatalogProduct>();
  const dictionary = store.catalogDictionary?.productsById ?? {};

  for (const symbol of store.symbolsMap.values()) {
    const furnitureProductId = symbol.threeD?.product?.productId?.trim();
    if (furnitureProductId) {
      const product = symbol.threeD?.product;
      upsertPlacedProduct(byId, {
        productId: furnitureProductId,
        name: product?.name,
        brand: product?.brand,
        supplierName: product?.supplierName,
        imageUrl:
          product?.selectedImageUrl ?? product?.asset?.thumbnailUrl,
        dictEntry: dictionary[furnitureProductId],
        source: "catalog",
        symbolId: symbol.id,
      });
      continue;
    }

    const userModel = symbol.threeD?.userModel;
    const userModelId = userModel?.modelId?.trim();
    if (userModelId) {
      // List user 3D objects with name + price; brand is optional (UI falls back).
      const userModelName = userModel?.name?.trim();
      const userModelBrand = userModel?.brand?.trim();
      const userModelPrice = userModel?.price;
      const hasCommerce =
        Boolean(userModelName) &&
        typeof userModelPrice === "number" &&
        Number.isFinite(userModelPrice);
      if (!hasCommerce) continue;

      upsertPlacedProduct(byId, {
        productId: userModelProductListId(userModelId),
        name: userModelName,
        brand: userModelBrand,
        // Match community library: prefer product photo, fall back to 3D thumb.
        imageUrl: resolveUserModel3DUrl(
          userModel?.sourceImageUrl || userModel?.thumbnailUrl,
        ),
        price: userModelPrice,
        currency: userModel?.currency?.trim() || "SEK",
        storeUrl: userModel?.urlStore,
        source: "userModel",
        symbolId: symbol.id,
      });
      continue;
    }

    // Flooring (and similar) applied as a product surface on area fills.
    const surfaceRef = symbol.threeD?.surfaceRef;
    if (surfaceRef?.source !== "product") continue;
    const surfaceProductId = surfaceRef.id?.trim();
    if (!surfaceProductId) continue;

    upsertPlacedProduct(byId, {
      productId: surfaceProductId,
      imageUrl: surfaceRef.imageUrl,
      dictEntry: dictionary[surfaceProductId],
      source: "catalog",
      symbolId: symbol.id,
    });
  }

  const wallsMap = store.wallsMap;
  if (wallsMap) {
    for (const wall of wallsMap.values()) {
      // Per-side walls can carry a different product on each side; count each
      // product once per wall (both sides with the same wallpaper = one wall).
      const surfaceRefs = wallHasPerSideFaceMaterials(wall)
        ? [
            resolveWallFaceSurfaceRef(wall, "left"),
            resolveWallFaceSurfaceRef(wall, "right"),
          ]
        : [wall.threeD?.surfaceRef];
      const seenProductIds = new Set<string>();
      for (const surfaceRef of surfaceRefs) {
        if (surfaceRef?.source !== "product") continue;
        const productId = surfaceRef.id?.trim();
        if (!productId || seenProductIds.has(productId)) continue;
        seenProductIds.add(productId);

        upsertPlacedProduct(byId, {
          productId,
          imageUrl: surfaceRef.imageUrl,
          dictEntry: dictionary[productId],
          source: "catalog",
          wallId: wall.id,
        });
      }
    }
  }

  return Array.from(byId.values());
}

/**
 * Estimated retail subtotal from cached catalog prices only.
 * Missing prices are skipped (no network) — callers should note partial totals.
 */
export function summarizePlacedCatalogPrices(
  products: PlacedCatalogProduct[],
): {
  total: number;
  pricedQuantity: number;
  missingPriceCount: number;
  currency: "SEK";
} {
  let total = 0;
  let pricedQuantity = 0;
  let missingPriceCount = 0;

  for (const entry of products) {
    if (typeof entry.price === "number" && Number.isFinite(entry.price)) {
      total += entry.price * entry.quantity;
      pricedQuantity += entry.quantity;
    } else {
      missingPriceCount += 1;
    }
  }

  return { total, pricedQuantity, missingPriceCount, currency: "SEK" };
}

/**
 * Unique catalog Product IDs for UserItem lineage / basket (excludes user 3D models).
 */
export function getPlacedCatalogProductIds(
  store: CatalogProductSource | FloorplannerStore,
): string[] {
  return collectPlacedCatalogProducts(store)
    .filter(
      (entry) =>
        entry.source !== "userModel" && !isUserModelProductListId(entry.productId),
    )
    .map((entry) => entry.productId);
}
